import type { AppliedCoupon } from '../types/coupon';
import type { CartItem } from '../types';
import type { CheckoutConsents } from '../types/consents';
import type { IyzicoInitiateRequest, IyzicoOrderRequest } from '../types/payment';
import { STORE_CURRENCY } from './formatPrice';
import { getUnitPrice } from './product';

const FREE_SHIPPING_THRESHOLD = 350;
const SHIPPING_COST = 15;

export function calculateShipping(subtotal: number) {
  return subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_COST;
}

export type OrderTotals = {
  subtotal: number;
  discountAmount: number;
  shipping: number;
  total: number;
};

export type CheckoutAddressState = {
  shippingMode: 'saved' | 'new';
  shippingAddressId?: string | null;
  saveShippingAddress?: boolean;
  billingSameAsShipping: boolean;
  billingMode?: 'saved' | 'new';
  billingAddressId?: string | null;
  saveBillingAddress?: boolean;
};

export function calculateOrderTotals(
  subtotal: number,
  appliedCoupon: AppliedCoupon | null,
): OrderTotals {
  const discountAmount = appliedCoupon?.discountAmount ?? 0;
  const shipping = calculateShipping(subtotal);
  const total = Math.max(0, subtotal - discountAmount + shipping);

  return { subtotal, discountAmount, shipping, total };
}

function readInlineShipping(form: FormData) {
  return {
    address: String(form.get('address') ?? ''),
    city: String(form.get('city') ?? ''),
    district: String(form.get('district') ?? ''),
    zip: String(form.get('zip') ?? ''),
  };
}

function readInlineBilling(form: FormData) {
  return {
    address: String(form.get('billingAddress') ?? ''),
    city: String(form.get('billingCity') ?? ''),
    district: String(form.get('billingDistrict') ?? ''),
    zip: String(form.get('billingZip') ?? ''),
  };
}

function readIdentityNumber(form: FormData): string {
  return String(form.get('identityNumber') ?? '').replace(/\D/g, '').slice(0, 11);
}

export function buildIyzicoInitiateRequest(
  items: CartItem[],
  totals: OrderTotals,
  form: FormData,
  consents: CheckoutConsents,
  couponCode?: string | null,
  addressState?: CheckoutAddressState,
): IyzicoInitiateRequest {
  const order = buildIyzicoOrderRequest(items, totals, form, consents, couponCode, addressState);

  return {
    amount: totals.total,
    currency: order.currency,
    order,
  };
}

export function buildIyzicoOrderRequest(
  items: CartItem[],
  totals: OrderTotals,
  form: FormData,
  consents: CheckoutConsents,
  couponCode?: string | null,
  addressState?: CheckoutAddressState,
): IyzicoOrderRequest {
  const firstName = String(form.get('firstName') ?? '');
  const lastName = String(form.get('lastName') ?? '');
  const shippingMode = addressState?.shippingMode ?? 'new';
  const billingSameAsShipping = addressState?.billingSameAsShipping ?? true;

  const order: IyzicoOrderRequest = {
    items: items.map(({ product, quantity }) => ({
      productId: product.id,
      quantity,
      price: getUnitPrice(product),
    })),
    subtotal: totals.subtotal,
    discountAmount: totals.discountAmount,
    couponCode: couponCode ?? null,
    shipping: totals.shipping,
    total: totals.total,
    currency: STORE_CURRENCY,
    billingSameAsShipping,
    contact: {
      firstName,
      lastName,
      email: String(form.get('email') ?? ''),
      phone: String(form.get('phone') ?? ''),
      identityNumber: readIdentityNumber(form),
    },
    consents,
  };

  if (shippingMode === 'saved' && addressState?.shippingAddressId) {
    order.shippingAddressId = addressState.shippingAddressId;
  } else {
    order.shippingAddress = readInlineShipping(form);
    if (addressState?.saveShippingAddress) {
      order.saveShippingAddress = true;
    }
  }

  if (!billingSameAsShipping) {
    const billingMode = addressState?.billingMode ?? 'new';
    if (billingMode === 'saved' && addressState?.billingAddressId) {
      order.billingAddressId = addressState.billingAddressId;
    } else {
      order.billingAddress = readInlineBilling(form);
      if (addressState?.saveBillingAddress) {
        order.saveBillingAddress = true;
      }
    }
  }

  return order;
}

export { FREE_SHIPPING_THRESHOLD, SHIPPING_COST };
